import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Menu, MenuItem, MenuList, Typography } from "@mui/material";
import {
  AccessTimeOutlined,
  BusinessOutlined,
  KeyboardArrowDown,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import UserContext from "common/Context/UserContext";
import INFRAMARK_CONSTANTS from "common/Constants/inframark-constants";
import {
  isPlatformAdminProfile,
  platformAdminUsesCustomerContextFromMe,
} from "common/Utilities/Role.utils";
import {
  filterSelectableCustomerAccessRows,
  isSelectableCustomerAccessRow,
} from "common/Utilities/CustomerSelection.utils";
import { applyNavigationAfterCustomerSwitch, resolveDefaultCustomerAdminPath } from "common/Utilities/EffectiveAccess.utils";
import { useHomePageHooks } from "pages/Home/Home.hook";
import HeaderClock from "./HeaderClock";
import {
  menuItemActiveSx,
  menuPaperSx,
  menuSectionLabelSx,
} from "../TopHeader.style";
import { useHeaderMenuAnchor } from "../useHeaderMenuAnchor";
import {
  buildingIconSx,
  chevronIconSx,
  customerButtonSx,
  customerIconSx,
  customerMenuContainerSx,
  customerMenuItemsScrollSx,
  customerMenuItemSx,
  customerMenuNameSx,
  customerMenuSubSx,
  customerMenuWidthSx,
  customerNameSx,
  separatorDotSx,
} from "./CustomerSwitcher.style";

const pickDefaultCustomerRow = (customerList, profile) => {
  const selectable = filterSelectableCustomerAccessRows(customerList);
  if (!selectable.length) return null;
  const id = profile?.activeCustomerId;
  const code = profile?.activeCustomerCode;
  if (id != null && String(id).trim() !== "") {
    return selectable.find((c) => String(c.id) === String(id)) ?? null;
  }
  if (code != null && String(code).trim() !== "") {
    return (
      selectable.find((c) => String(c.customerCode) === String(code)) ?? null
    );
  }
  return selectable[0] ?? null;
};

const resolveSelectableCustomer = (customer, selectableList) => {
  if (!customer || !selectableList?.length) {
    return null;
  }
  const code = customer.customerCode;
  return (
    selectableList.find(
      (c) =>
        String(c.id) === String(customer.id) ||
        String(c.customerCode) === String(code)
    ) ?? null
  );
};

const resolveCustomerRow = (customerList, customerInfo) => {
  if (!customerInfo || !customerList?.length) {
    return customerInfo;
  }
  return (
    customerList.find(
      (c) =>
        String(c.id) === String(customerInfo.id) ||
        String(c.customerCode) === String(customerInfo.customerCode)
    ) ?? customerInfo
  );
};

/**
 * Customer switcher — same selection / switch / navigation behavior as the
 * pre-facelift SelectedCustomerDropDown.
 */
const CustomerSwitcher = () => {
  const navigate = useNavigate();
  const {
    customerList,
    userProfile,
    headerSelectedCustomer,
    setHeaderSelectedCustomer,
    switchActiveCustomer,
  } = useContext(UserContext);
  const { actions } = useHomePageHooks();
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isSwitchingCustomer, setIsSwitchingCustomer] = useState(false);
  const previousCustomerRef = useRef(null);
  const { anchorEl, isOpen, handleToggle, handleClose, closeMenu } =
    useHeaderMenuAnchor("customer");

  const profile = userProfile?.profile;
  const timezone = selectedCustomer?.timeZone || "";
  const showClock = Boolean(timezone);

  const selectableCustomerList = useMemo(
    () => filterSelectableCustomerAccessRows(customerList ?? []),
    [customerList]
  );

  useEffect(() => {
    if (!customerList?.length) {
      setSelectedCustomer(null);
      return;
    }
    if (isPlatformAdminProfile(profile)) {
      const nextSelection = platformAdminUsesCustomerContextFromMe(profile)
        ? resolveSelectableCustomer(
            headerSelectedCustomer,
            selectableCustomerList
          )
        : null;
      setSelectedCustomer(nextSelection);
      return;
    }
    const candidate =
      headerSelectedCustomer ?? pickDefaultCustomerRow(customerList, profile);
    setSelectedCustomer(
      resolveSelectableCustomer(candidate, selectableCustomerList)
    );
  }, [customerList, profile, headerSelectedCustomer, selectableCustomerList]);

  const handleSelect = async (selectedCustomerObj) => {
    closeMenu();
    if (
      !selectedCustomerObj ||
      !isSelectableCustomerAccessRow(selectedCustomerObj) ||
      isSwitchingCustomer
    ) {
      return;
    }
    if (
      selectedCustomer &&
      String(selectedCustomer.id) === String(selectedCustomerObj.id)
    ) {
      return;
    }

    previousCustomerRef.current = selectedCustomer;
    setIsSwitchingCustomer(true);
    setHeaderSelectedCustomer(selectedCustomerObj);
    setSelectedCustomer(selectedCustomerObj);

    try {
      if (profile?.sessionFromMe) {
        const result = await switchActiveCustomer(selectedCustomerObj);
        if (result?.error) {
          const previous = previousCustomerRef.current;
          setSelectedCustomer(previous);
          setHeaderSelectedCustomer(previous);
          return;
        }

        const syncedCustomer = resolveCustomerRow(
          customerList,
          result?.customerInfo ?? selectedCustomerObj
        );
        actions.setSlectedCustomer(syncedCustomer, profile?.userid);
        setHeaderSelectedCustomer(syncedCustomer);
        setSelectedCustomer(syncedCustomer);

        window.dispatchEvent(
          new CustomEvent("customerChanged", { detail: syncedCustomer })
        );
        applyNavigationAfterCustomerSwitch(profile, navigate);
      } else {
        actions.setSlectedCustomer(selectedCustomerObj, profile?.userid);
        window.dispatchEvent(
          new CustomEvent("customerChanged", { detail: selectedCustomerObj })
        );
        navigate(resolveDefaultCustomerAdminPath(profile));
      }
    } finally {
      setIsSwitchingCustomer(false);
    }
  };

  return (
    <>
      <Button
        onClick={handleToggle}
        title="Switch customer"
        aria-haspopup="menu"
        aria-expanded={isOpen ? "true" : undefined}
        disabled={isSwitchingCustomer}
        sx={customerButtonSx}
      >
        {showClock ? (
          <>
            <AccessTimeOutlined sx={customerIconSx} />
            <HeaderClock timezone={timezone} />
            <Box component="span" sx={separatorDotSx}>
              ·
            </Box>
          </>
        ) : null}
        <BusinessOutlined sx={buildingIconSx} />
        <Box component="span" sx={customerNameSx}>
          {selectedCustomer?.name ||
            INFRAMARK_CONSTANTS.selectedCustomer.noCustomerSelected}
        </Box>
        <KeyboardArrowDown sx={chevronIconSx} />
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: { sx: { ...menuPaperSx, ...customerMenuWidthSx } },
          list: {
            component: "div",
            sx: customerMenuContainerSx,
          },
        }}
      >
        <Typography component="div" sx={menuSectionLabelSx}>
          Switch customer
        </Typography>
        <MenuList sx={customerMenuItemsScrollSx} disablePadding autoFocusItem>
          {selectableCustomerList.length === 0 ? (
            <MenuItem disabled sx={customerMenuItemSx}>
              <Typography component="span" sx={customerMenuNameSx}>
                {INFRAMARK_CONSTANTS.selectedCustomer.noCustomerSelected}
              </Typography>
            </MenuItem>
          ) : (
            selectableCustomerList.map((customer) => {
              const isActive =
                selectedCustomer &&
                String(selectedCustomer.id) === String(customer.id);
              return (
                <MenuItem
                  key={customer.id}
                  onClick={() => handleSelect(customer)}
                  sx={{
                    ...customerMenuItemSx,
                    ...(isActive ? menuItemActiveSx : {}),
                  }}
                >
                  <Typography component="span" sx={customerMenuNameSx}>
                    {customer.name}
                  </Typography>
                  {customer.customerCode ? (
                    <Typography component="span" sx={customerMenuSubSx}>
                      {customer.customerCode}
                    </Typography>
                  ) : null}
                </MenuItem>
              );
            })
          )}
        </MenuList>
      </Menu>
    </>
  );
};

export default CustomerSwitcher;
